export type BillingCycle = "MONTHLY" | "ANNUAL";
export type SupportLevel = "NONE" | "STANDARD" | "PRIORITY";

/** Plano exibido na vitrine (GET /plans/public). */
export type PublicPlan = {
  id: string;
  name: string;
  /** Preço em centavos */
  price: number;
  description: string | null;
  billing_cycle: BillingCycle;
  trial_days: number;
  max_products: number | null;
  max_categories: number | null;
  max_users: number | null;
  max_establishments: number | null;
  has_whatsapp_bot: boolean;
  has_printing: boolean;
  has_motoboy_app: boolean;
  has_waiter_app: boolean;
  has_admin_app: boolean;
  has_cash_management: boolean;
  has_table_qrcode: boolean;
  has_inventory: boolean;
  has_multi_store: boolean;
  has_custom_digital_menu: boolean;
  has_online_payment: boolean;
  has_coupons: boolean;
  has_ifood_integration: boolean;
  has_99food_integration: boolean;
  support_level: SupportLevel;
};

export type PlanFeature = {
  label: string;
  included: boolean;
  /** Recurso ainda em desenvolvimento */
  comingSoon?: boolean;
};

const priceFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

export function formatPlanPrice(cents: number) {
  return priceFormatter.format(cents / 100);
}

export function getBillingSuffix(cycle: BillingCycle) {
  return cycle === "ANNUAL" ? "/ano" : "/mês";
}

function formatLimit(value: number | null, singular: string, plural: string, unlimited: string) {
  if (value == null) return unlimited;
  return `Até ${value} ${value === 1 ? singular : plural}`;
}

const SUPPORT_LABEL: Record<SupportLevel, string> = {
  NONE: "Sem suporte",
  STANDARD: "Suporte humano",
  PRIORITY: "Suporte prioritário",
};

/**
 * Recursos do plano na ordem de exibição: incluídos primeiro, depois os
 * não incluídos (para o visitante comparar os planos).
 */
export function buildPlanFeatures(plan: PublicPlan): PlanFeature[] {
  const features: PlanFeature[] = [
    {
      label: formatLimit(plan.max_establishments, "loja", "lojas", "Lojas ilimitadas"),
      included: true,
    },
    {
      label: formatLimit(plan.max_products, "produto", "produtos", "Produtos ilimitados"),
      included: true,
    },
    {
      label: formatLimit(plan.max_users, "usuário", "usuários", "Usuários ilimitados"),
      included: true,
    },
    { label: "Cardápio digital personalizado", included: plan.has_custom_digital_menu },
    { label: "Gestão de caixa", included: plan.has_cash_management },
    { label: "Controle de estoque", included: plan.has_inventory },
    { label: "QR Code nas mesas", included: plan.has_table_qrcode },
    { label: "Impressão automática de pedidos", included: plan.has_printing },
    { label: "App do garçom", included: plan.has_waiter_app },
    { label: "App do motoboy", included: plan.has_motoboy_app },
    { label: "App admin para o lojista", included: plan.has_admin_app },
    { label: "Gestão multilojas", included: plan.has_multi_store },
    { label: "Bot de atendimento no WhatsApp", included: plan.has_whatsapp_bot },
    { label: "Pagamento online", included: plan.has_online_payment, comingSoon: true },
    { label: "Cupons de desconto", included: plan.has_coupons, comingSoon: true },
    { label: "Integração com iFood", included: plan.has_ifood_integration, comingSoon: true },
    { label: "Integração com 99Food", included: plan.has_99food_integration, comingSoon: true },
    {
      label: SUPPORT_LABEL[plan.support_level] ?? SUPPORT_LABEL.STANDARD,
      included: plan.support_level !== "NONE",
    },
  ];

  return [...features.filter((f) => f.included), ...features.filter((f) => !f.included)];
}
